import React, { useState, useEffect, useCallback } from 'react';
import WalterLoader from '../shared/WalterLoader';
import { FaTrash, FaExclamationCircle, FaChevronDown } from 'react-icons/fa';
import { useAppMetadata } from '../shared/useAppMetadata';
import { slugify } from '../../utils/slugify';
import '../../styles/Admin.css';

const API_URL = process.env.REACT_APP_API_URL;

const FIELD_TYPES = [
    { value: 'number', label: 'Nombre' },
    { value: 'checkbox', label: 'Case à cocher' },
    { value: 'text', label: 'Texte' },
];

const putJson = (path, body) => fetch(`${API_URL}${path}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
});

/* ─────────────────────────────────────────────
   Écran d'admin pour la liste des types de machine (app_metadata,
   type="machine_types") et leurs champs techniques personnalisés — sert de
   base au rendu dynamique des champs dans MachineFields.js. Calqué sur
   BuildingTypes.js : le `value` (slug utilisé sur equipments.technical_data.
   type_machine) et le `key` de chaque champ personnalisé (utilisé sur
   technical_data.custom_fields) ne sont jamais modifiables après création —
   seuls les libellés le sont — pour ne jamais casser un équipement existant.
   Suppression d'un type bloquée tant qu'au moins un équipement l'utilise.
   Pas de réordonnancement manuel : les listes suivent l'ordre de création.
───────────────────────────────────────────── */
const MachineTypes = () => {
    const { data: machineTypes, loading, reload } = useAppMetadata('machine_types');
    const [counts, setCounts] = useState({});
    const [newLabel, setNewLabel] = useState('');
    const [expanded, setExpanded] = useState(null);
    const [error, setError] = useState(null);

    const loadCounts = useCallback(() => {
        fetch(`${API_URL}/get_machine_type_counts`)
            .then((r) => r.json())
            .then((json) => setCounts(json && typeof json === 'object' ? json : {}))
            .catch(() => setCounts({}));
    }, []);

    useEffect(() => { loadCounts(); }, [loadCounts]);

    const saveList = async (nextList) => {
        setError(null);
        const res = await putJson('/set_app_metadata/machine_types', nextList);
        if (!res.ok) {
            setError("Erreur lors de l'enregistrement.");
            return;
        }
        await reload();
        loadCounts();
    };

    const addType = () => {
        const label = newLabel.trim();
        if (!label) return;
        const value = slugify(label);
        if (machineTypes.some((t) => t.value === value)) {
            setError('Ce type existe déjà.');
            return;
        }
        saveList([...machineTypes, { value, label, description: '', default_volume_unit: 'L/cycle', fields: [] }]);
        setNewLabel('');
    };

    const updateType = (value, patch) => {
        saveList(machineTypes.map((t) => (t.value === value ? { ...t, ...patch } : t)));
    };

    const deleteType = (value) => {
        if (counts[value]) return;
        if (!window.confirm('Supprimer ce type de machine ?')) return;
        saveList(machineTypes.filter((t) => t.value !== value));
    };

    const addField = (type) => {
        const label = window.prompt('Nom du champ (ex. Capacité) ?');
        if (!label || !label.trim()) return;
        const key = slugify(label.trim()).replace(/-/g, '_');
        if (type.fields.some((f) => f.key === key)) {
            setError('Ce champ existe déjà pour ce type.');
            return;
        }
        const field = { key, label: label.trim(), type: 'number', unit: '', required: false };
        updateType(type.value, { fields: [...type.fields, field] });
    };

    const updateField = (type, key, patch) => {
        updateType(type.value, {
            fields: type.fields.map((f) => (f.key === key ? { ...f, ...patch } : f)),
        });
    };

    const removeField = (type, key) => {
        if (!window.confirm('Supprimer ce champ ? Les valeurs déjà saisies pour les équipements existants seront ignorées.')) return;
        updateType(type.value, { fields: type.fields.filter((f) => f.key !== key) });
    };

    if (loading) {
        return (
            <div className="centered-spinner-dashboard">
                <WalterLoader />
            </div>
        );
    }

    return (
        <div className="admin-page-content admin-page-content--narrow-top">
            {error && (
                <div className="admin-error-banner"><FaExclamationCircle />{error}</div>
            )}

            <div className="admin-card">
                <div className="admin-card-header">
                    <span className="admin-card-title">
                        <span className="admin-card-title-bar" />
                        Types disponibles
                    </span>
                    <span className="admin-count-badge">{machineTypes.length}</span>
                </div>

                {machineTypes.length === 0 && (
                    <div className="admin-empty-state">Aucun type de machine pour l'instant.</div>
                )}

                {machineTypes.map((type) => {
                    const count = counts[type.value] || 0;
                    const isOpen = expanded === type.value;
                    return (
                        <div key={type.value}>
                            <div className="admin-row">
                                <input
                                    type="text" className="admin-row-input" defaultValue={type.label}
                                    onBlur={(e) => e.target.value !== type.label && updateType(type.value, { label: e.target.value })}
                                />
                                <span className="admin-slug-tag">{type.value}</span>
                                <div className="admin-row-meta">
                                    <span className="admin-usage-count">
                                        {count} équipement{count > 1 ? 's' : ''}
                                    </span>
                                    <button type="button" className={`admin-expand-btn${isOpen ? ' open' : ''}`}
                                        onClick={() => setExpanded(isOpen ? null : type.value)}>
                                        Champs ({type.fields.length}) <FaChevronDown />
                                    </button>
                                    <button type="button" className="admin-icon-btn admin-icon-btn--danger"
                                        onClick={() => deleteType(type.value)} disabled={count > 0}
                                        title={count > 0 ? 'Reclasser les équipements concernés avant de supprimer ce type' : 'Supprimer'}>
                                        <FaTrash />
                                    </button>
                                </div>
                            </div>

                            {isOpen && (
                                <div className="admin-subpanel">
                                    <div className="admin-subpanel-field">
                                        <span className="admin-subpanel-label">Description</span>
                                        <textarea
                                            defaultValue={type.description}
                                            placeholder="Description de ce type de machine"
                                            onBlur={(e) => e.target.value !== type.description && updateType(type.value, { description: e.target.value })}
                                            style={{ width: '100%', minHeight: '50px', resize: 'vertical' }}
                                        />
                                    </div>

                                    <div className="admin-subpanel-field">
                                        <span className="admin-subpanel-label">
                                            Unité de volume par défaut
                                            <span className="admin-subpanel-hint"> — présélectionnée dans le formulaire pour ce type (ex. Karcher → L/min), modifiable équipement par équipement</span>
                                        </span>
                                        <select
                                            value={type.default_volume_unit || 'L/cycle'}
                                            onChange={(e) => updateType(type.value, { default_volume_unit: e.target.value })}
                                            style={{ maxWidth: '280px' }}
                                        >
                                            <option value="L/cycle">L/cycle (volume par cycle)</option>
                                            <option value="L/min">L/min (débit)</option>
                                        </select>
                                    </div>

                                    <div className="admin-subpanel-field">
                                        <span className="admin-subpanel-label">Champs techniques</span>
                                        <div className="admin-field-list">
                                            {type.fields.length === 0 && (
                                                <div className="admin-empty-state" style={{ padding: '10px 0' }}>Aucun champ personnalisé.</div>
                                            )}
                                            {type.fields.map((field) => (
                                                <div key={field.key} className="admin-field-row">
                                                    <input
                                                        type="text" className="admin-field-name" defaultValue={field.label}
                                                        onBlur={(e) => e.target.value !== field.label && updateField(type, field.key, { label: e.target.value })}
                                                    />
                                                    <span className="admin-slug-tag">{field.key}</span>
                                                    <select
                                                        value={field.type}
                                                        onChange={(e) => updateField(type, field.key, { type: e.target.value })}
                                                    >
                                                        {FIELD_TYPES.map((ft) => <option key={ft.value} value={ft.value}>{ft.label}</option>)}
                                                    </select>
                                                    {field.type === 'number' && (
                                                        <input
                                                            type="text" className="admin-field-unit" defaultValue={field.unit}
                                                            placeholder="unité (ex. kg)"
                                                            onBlur={(e) => e.target.value !== field.unit && updateField(type, field.key, { unit: e.target.value })}
                                                        />
                                                    )}
                                                    <label className="admin-field-required">
                                                        <input
                                                            type="checkbox" checked={!!field.required}
                                                            onChange={(e) => updateField(type, field.key, { required: e.target.checked })}
                                                        />
                                                        requis
                                                    </label>
                                                    <button type="button" className="admin-icon-btn admin-icon-btn--danger"
                                                        onClick={() => removeField(type, field.key)} title="Supprimer ce champ">
                                                        <FaTrash />
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                        <button type="button" className="admin-add-field-btn" onClick={() => addField(type)}>
                                            + Ajouter un champ
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}

                <div className="admin-add-row">
                    <input
                        type="text" placeholder="Nouveau type de machine"
                        value={newLabel}
                        onChange={(e) => setNewLabel(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && addType()}
                    />
                    <button type="button" className="admin-add-btn" onClick={addType} disabled={!newLabel.trim()}>
                        + Ajouter
                    </button>
                </div>
            </div>
        </div>
    );
};

export default MachineTypes;
